import { useState, useMemo, useEffect, type ReactNode } from "react";
import { Routes, Route, useNavigate, Navigate, useLocation } from "react-router-dom";
import { Sidebar } from "./components/Sidebar";
import { DashboardView } from "./views/DashboardView";
import { PlayerDataView } from "./views/PlayerDataView";
import { RelationshipsView } from './views/RelationshipsView';
import { LogbookView } from './views/LogbookView';
import { LogbookEventView } from './views/LogbookEventView';
import { LogbookCasesView } from './views/LogbookCasesView';
import { LogbookCaseDetailView } from './views/LogbookCaseDetailView';
import { LogbookHealthView } from './views/LogbookHealthView';
import { LogbookNearbyView } from './views/LogbookNearbyView';
import { SettingsView } from "./views/SettingsView";
import { EconomyView } from "./views/EconomyView";
import { WarpView } from "./views/WarpView";
import { PlayerStreamLogView } from "./views/PlayerStreamLogView";
import { NotesWarningsView } from "./views/NotesWarningsView";
import { SearchModal } from "./views/SearchModal";
import { StatusMonitorView } from "./views/StatusMonitorView";
import { StarchartView } from "./views/StarchartView";
import { StarchartServerView } from "./views/StarchartServerView";
import { BansView } from './views/BansView';
import { EvidenceView } from './views/EvidenceView';
import { IncidentsListView } from './views/IncidentsListView';
import { IncidentDetailView } from './views/IncidentDetailView';
import { CalendarListView } from './views/CalendarListView';
import { AtlasView } from './views/AtlasView';
import { StaffManagerView } from './views/StaffManagerView';
import { StaffMigrationsView } from './views/StaffMigrationsView';
import { ModpackProvisioningView } from './views/ModpackProvisioningView';
import { StaffProfileView } from './views/StaffProfileView';
import { StaffTeamsView } from './views/StaffTeamsView';
import { StaffTicketsView } from './views/StaffTicketsView';
import { StaffNotesView } from './views/StaffNotesView';
import { StaffActivityView } from './views/StaffActivityView';
import { DataControllerView } from './views/DataControllerView';
import { VotesView } from './views/VotesView';
import { UpdateAdvisorView } from './views/UpdateAdvisorView';
import { ItemBansView } from './views/ItemBansView';
import { SpawnLimitsView } from './views/SpawnLimitsView';
import { ModpackConfigsView } from './views/ModpackConfigsView';
import { ModpackConfigDetailView } from './views/ModpackConfigDetailView';
import { ScheduledCommandsView } from './views/ScheduledCommandsView';
import { ScheduledCommandEditorView } from './views/ScheduledCommandEditorView';
import { TellrawLibraryEntryView } from './views/TellrawLibraryEntryView';
import { ModpackNotesView } from './views/ModpackNotesView';
import { PartnershipsView } from './views/PartnershipsView';
import { PartnershipDetailView } from './views/PartnershipDetailView';
import { PartnerView } from './views/PartnerView';
import { BuildTeamView } from './views/BuildTeamView';
import { BuildProjectDetailView } from './views/BuildProjectDetailView';
import { BuildItemDetailView } from './views/BuildItemDetailView';
import { EventTeamView } from './views/EventTeamView';
import { FormsView } from './views/FormsView';
import { FormDetailView } from './views/FormDetailView';
import { formPermissions } from './features/forms/permissions';
import { EventItemDetailView } from './views/EventItemDetailView';
import { EventProjectDetailView } from './views/EventProjectDetailView';
import { EventMemberDetailView } from './views/EventMemberDetailView';
import { EventCheckDetailView } from './views/EventCheckDetailView';
import { QuestViewerDevView } from './views/QuestViewerDevView';
import { AppealsView } from './views/AppealsView';
import { AppealDetailView } from './views/AppealDetailView';
import { ModpackBrowserView } from './views/ModpackBrowserView';
import { ModpackDetailView } from './views/ModpackDetailView';
import { WorkspacesView } from './views/WorkspacesView';
import { WorkspaceBoardView } from './views/WorkspaceBoardView';
import { ChunkThrottleView } from './views/ChunkThrottleView';
import { ChunkThrottleServerView } from './views/ChunkThrottleServerView';
import { Search, Menu } from "lucide-react";
import { GalaxyBackground } from "./components/galaxy/GalaxyBackground";
import { ChatLogView } from './views/ChatLogView';
import { HistoricCommandLogView } from './views/HistoricCommandLogView';
import { PlayerSpawnsView } from './views/PlayerSpawnsView';
import { ShopChangelogView } from './views/ShopChangelogView';
import { ChatTabsProvider } from "./contexts/ChatTabsContext";
import { CommandLogTabsProvider } from './contexts/CommandLogTabsContext';
import { ConfidentialityWarning } from "./components/ConfidentialityWarning";
import { IPWatermark } from "./components/IPWatermark";
import { StaffScreenMark } from "./components/StaffScreenMark";
import { MobileBottomNav } from "./components/MobileBottomNav";
import { cn } from "./utils/cn";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { UserPreferencesProvider, useUserPreferences } from './contexts/UserPreferencesContext';
import { LightBackground } from './components/LightBackground';
import { useIsMobile } from './hooks/useIsMobile';
import {
  getPageTitleForPath,
  getVisibleNavigation,
  getVisibleNavigationItems,
  isNavItemActive,
} from "./config/navigation";
import { useNavFavorites } from "./hooks/useNavFavorites";
import { getLimitedTesseractTeamAccess, getTeamScopedStaff, TESSERACT_TEAM_CURATORS } from "./utils/tesseractTeamAccess";
import { canViewStaffPanel } from "./utils/staffPermissions";
import { canAccessDataController } from "./utils/dataControllerPermissions";
import { canViewModpackConfigs } from './utils/modpackConfigPermissions';
import { canViewScheduledCommands } from './utils/scheduledCommandPermissions';
import { canViewPartnerships } from './utils/partnershipPermissions';
import { canViewRelationships } from './utils/relationshipPermissions';
import { canAdministerLogbook, canViewLogbook } from './utils/logbookPermissions';
import { canViewKanbanWorkspaces } from './utils/kanbanPermissions';

function LimitedAccessBoundary({
  identity,
  isDev,
  children,
}: {
  identity: ReturnType<typeof useAuth>["identity"];
  isDev: boolean;
  children: ReactNode;
}) {
  const location = useLocation();
  if (!getTeamScopedStaff(identity, isDev)) return <>{children}</>;

  // Team-scoped staff reach the dashboard, settings and exactly the panels their sidebar offers
  // (their team panels plus anything granted explicitly); every other URL goes to the dashboard.
  // Workspaces stays available for direct board shares; its API authorizes every visible board.
  const path = location.pathname;
  const ok =
    path === "/" ||
    ["/dashboard", "/settings"].some((prefix) => path === prefix || path.startsWith(`${prefix}/`)) ||
    getVisibleNavigationItems({ identity, isDev }).some((item) => isNavItemActive(item, path));
  if (ok) return <>{children}</>;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    return JSON.parse(localStorage.getItem('sidebar-pinned') || 'false');
  });
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { identity, isLoading: isAuthLoading } = useAuth();
  const { securityAccessibilityMode, isReady: preferencesReady, isLoading: preferencesLoading, error: preferencesError, retry: retryPreferences } = useUserPreferences();
  const { theme, toggleTheme, galaxyEnabled, toggleGalaxy } = useTheme();
  const isMobile = useIsMobile();

  const isDev = import.meta.env.DEV;

  const limitedTesseractTeamAccess = useMemo(
    () => getLimitedTesseractTeamAccess(identity, isDev),
    [identity, isDev],
  );
  // Builders, event team, newsletter and curators with no other staff team: no player search.
  const isTeamScopedStaff = useMemo(
    () => getTeamScopedStaff(identity, isDev) != null,
    [identity, isDev],
  );

  const navigationContext = useMemo(
    () => ({ identity, isDev }),
    [identity, isDev]
  );

  const visibleNavigationCategories = useMemo(
    () => getVisibleNavigation(navigationContext),
    [navigationContext]
  );
  const visibleNavigationItems = useMemo(
    () => getVisibleNavigationItems(navigationContext),
    [navigationContext]
  );

  const {
    favoriteItems,
    isFavorite,
    toggleFavorite,
    reorderFavorites,
  } = useNavFavorites({
    visibleItems: visibleNavigationItems,
    userUuid: identity?.user_uuid,
  });

  const pageTitle = useMemo(
    () => getPageTitleForPath(location.pathname),
    [location.pathname]
  );
  // Animate content in when moving between sections (not on param-only changes).
  const pageSectionKey = location.pathname.split('/')[1] || 'home';

  const canViewIncidents = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('SPY_Notes') || groups.includes('SPY_Notes_Editor');
  }, [identity, isDev]);

  const canViewCalendar = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    if (limitedTesseractTeamAccess && limitedTesseractTeamAccess.role !== TESSERACT_TEAM_CURATORS) return true;
    const permissions = identity.raw.custom.permissions || [];
    return permissions.includes('argus.calendar.view');
  }, [identity, isDev, limitedTesseractTeamAccess]);

  const canViewAtlas = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const permissions = identity.raw.custom.permissions || [];
    return permissions.includes('argus.atlas.read');
  }, [identity, isDev]);

  const canViewStaff = useMemo(() => canViewStaffPanel(identity, isDev), [identity, isDev]);
  const canUseDataController = useMemo(() => canAccessDataController(identity, isDev), [identity, isDev]);
  const canExploreRelationships = canViewRelationships(identity);
  // Evidence access is auditable, so there is no development bypass: the permission node decides.
  const canReadLogbook = canViewLogbook(identity);
  const canAdministerEvidence = canAdministerLogbook(identity);

  const canViewModpackNotes = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('argus_modpack_read') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  // Shared with the sidebar entry so the route guard and the nav agree.
  const canViewModpackConfigsHere = useMemo(
    () => isDev || canViewModpackConfigs(identity),
    [identity, isDev],
  );

  // Shared with the sidebar entry, like the modpack configs guard above.
  const canViewScheduledCommandsHere = useMemo(
    () => isDev || canViewScheduledCommands(identity),
    [identity, isDev],
  );

  // Shared with the sidebar entry, like the modpack configs guard above.
  const canViewPartnershipsHere = useMemo(
    () => isDev || canViewPartnerships(identity),
    [identity, isDev],
  );

  const canViewBuildTeam = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    if (
      limitedTesseractTeamAccess &&
      !limitedTesseractTeamAccess.calendarViewOnly &&
      limitedTesseractTeamAccess.role === "buildteam"
    ) {
      return true;
    }
    const groups = identity.raw.custom.groups;
    return groups.includes('argus_build_panel') || groups.includes('argus_build_lead') || groups.includes('argus_build_admin') || groups.includes('SPY_Super_admin');
  }, [identity, isDev, limitedTesseractTeamAccess]);

  const canViewEventTeam = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    if (
      limitedTesseractTeamAccess &&
      !limitedTesseractTeamAccess.calendarViewOnly &&
      limitedTesseractTeamAccess.role === "eventteam"
    ) {
      return true;
    }
    const groups = identity.raw.custom.groups;
    return groups.includes('argus_event_panel') || groups.includes('argus_event_lead') || groups.includes('argus_event_admin') || groups.includes('SPY_Super_admin');
  }, [identity, isDev, limitedTesseractTeamAccess]);

  const canViewAppeals = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('SPY_Bans_Helper') || groups.includes('SPY_Bans_Mod') || groups.includes('SPY_Bans_Admin') || groups.includes('SPY_Bans_Superadmins') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  const canViewChunkThrottle = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('SPY_Bans_Mod') || groups.includes('SPY_Bans_Admin') || groups.includes('SPY_Bans_Superadmins') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  const canViewWorkspaces = canViewKanbanWorkspaces(identity, isDev);

  // ⌘K / Ctrl+K opens the player search anywhere (full-access users only).
  useEffect(() => {
    if (isTeamScopedStaff) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsSearchModalOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isTeamScopedStaff]);

  const openSearchModalWithQuery = (query: string) => {
    setSearchQuery(query);
    setIsSearchModalOpen(true);
  };

  const handleSearchResultClick = (uuid: string) => {
    setIsSearchModalOpen(false);
    navigate(`/player/${uuid}`);
  };

  if (isAuthLoading) {
    return (
      <div className="w-full h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 animate-fade-in">
          <img
            src="https://cdn.playcdu.co/Images/Branding/Blue/BH_NU_Asset_6.png"
            alt="Argus"
            className="w-12 h-12 object-contain"
          />
          <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
            <span className="w-3.5 h-3.5 rounded-full border-2 border-[var(--border-strong)] border-t-[var(--accent-300)] animate-spin" />
            Loading user identity...
          </div>
        </div>
      </div>
    );
  }

  // Do not expose marked content until the account's saved pattern is known. In particular,
  // someone who enabled accessibility mode must not briefly see dots during startup.
  if (identity && !preferencesReady) {
    return (
      <div className="w-full h-screen bg-[var(--bg-primary)] flex items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-4 text-center">
          <img src="https://cdn.playcdu.co/Images/Branding/Blue/BH_NU_Asset_6.png" alt="Argus" className="w-12 h-12 object-contain" />
          {preferencesError ? (
            <>
              <p role="alert" className="text-sm text-[var(--text-secondary)]">{preferencesError}</p>
              <button type="button" className="glass-button" disabled={preferencesLoading} onClick={() => void retryPreferences()}>
                Retry loading preferences
              </button>
            </>
          ) : (
            <p role="status" className="text-sm text-[var(--text-muted)]">Loading your account preferences...</p>
          )}
        </div>
      </div>
    );
  }

  const isLight = theme === 'light';
  const effectiveGalaxyEnabled = !securityAccessibilityMode && !isLight && galaxyEnabled && !isMobile;

  return (
    <div className={cn(
      "relative w-full h-screen",
      !effectiveGalaxyEnabled && "bg-[var(--bg-primary)]"
    )}>
      <ConfidentialityWarning />
      <IPWatermark />
      <StaffScreenMark />
      {isLight && !securityAccessibilityMode && <LightBackground />}
      {effectiveGalaxyEnabled && (
        <GalaxyBackground
          autoRotate={true}
          className="absolute top-0 left-0 w-full h-full -z-10"
        />
      )}
      <div className="absolute top-0 left-0 w-full h-full flex text-white">
        <Sidebar 
          isOpen={isSidebarOpen} 
          setIsOpen={setIsSidebarOpen}
          onSearchClick={() => setIsSearchModalOpen(true)}
          theme={theme}
          onToggleTheme={toggleTheme}
          galaxyEnabled={galaxyEnabled}
          onToggleGalaxy={toggleGalaxy}
          teamScoped={isTeamScopedStaff}
          navigationCategories={visibleNavigationCategories}
          favoriteItems={favoriteItems}
          isFavorite={isFavorite}
          onToggleFavorite={toggleFavorite}
          onReorderFavorites={reorderFavorites}
        />
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {isMobile && (
            <div className="flex-shrink-0 h-14 px-2 flex items-center justify-between bg-[var(--surface-drawer)] backdrop-blur-xl border-b border-glass-200/10 mobile-header">
              <div className="flex items-center min-w-0 gap-1">
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="btn-icon touch-manipulation"
                  disabled={isSidebarOpen}
                  title="Open menu"
                  aria-label="Open menu"
                >
                  <Menu className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2 min-w-0">
                  <img
                    src="https://cdn.playcdu.co/Images/Branding/Blue/BH_NU_Asset_6.png"
                    alt="Argus"
                    className="w-6 h-6 flex-shrink-0 object-contain"
                  />
                  <h1 className="text-sm font-semibold tracking-tight text-white truncate">{pageTitle}</h1>
                </div>
              </div>
              {!isTeamScopedStaff && (
                <button
                  onClick={() => setIsSearchModalOpen(true)}
                  className="btn-icon touch-manipulation flex-shrink-0"
                  title="Search"
                  aria-label="Search"
                >
                  <Search className="w-5 h-5" />
                </button>
              )}
            </div>
          )}

          <main className={cn(
            "flex-1 overflow-y-auto p-3 md:p-6",
            isMobile && "pb-[calc(5rem+env(safe-area-inset-bottom))]"
          )}>
            <LimitedAccessBoundary identity={identity} isDev={isDev}>
            <div key={pageSectionKey} className="animate-page-in">
            <Routes>
              {formPermissions(identity, isDev).read && <><Route path="/forms" element={<FormsView />} /><Route path="/forms/:id" element={<FormDetailView />} /></>}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardView identity={identity} teamScoped={isTeamScopedStaff} />} />
              <Route path="/status-monitor" element={<StatusMonitorView />} />
              <Route path="/starchart" element={<StarchartView />} />
              <Route path="/starchart/:serverId" element={<StarchartServerView />} />
              <Route path="/player/:uuid" element={<PlayerDataView openSearchModalWithIp={openSearchModalWithQuery} user={identity} />} />
              <Route path="/player" element={<PlayerDataView openSearchModalWithIp={openSearchModalWithQuery} user={identity} />} />
              {canExploreRelationships && (
                <Route path="/relationships" element={<RelationshipsView />} />
              )}
              {canReadLogbook && (
                <>
                  <Route path="/logbook" element={<LogbookView />} />
                  <Route path="/logbook/nearby" element={<LogbookNearbyView />} />
                  <Route path="/logbook/cases" element={<LogbookCasesView />} />
                  <Route path="/logbook/cases/:caseId" element={<LogbookCaseDetailView />} />
                  <Route path="/logbook/event/:eventId" element={<LogbookEventView />} />
                  {canAdministerEvidence && <Route path="/logbook/health" element={<LogbookHealthView />} />}
                </>
              )}
              <Route path="/settings" element={<SettingsView />} />
              <Route path="/command-log" element={<HistoricCommandLogView />} />
              <Route path="/player-spawns" element={<PlayerSpawnsView />} />
              <Route path="/shop-changelog" element={<ShopChangelogView />} />
              <Route path="/chat/:clusterId" element={<ChatLogView />} />
              <Route path="/chat" element={<ChatLogView />} />
              <Route path="/economy/:clusterId" element={<EconomyView />} />
              <Route path="/economy" element={<EconomyView />} />
              <Route path="/warps" element={<WarpView />} />
              <Route path="/item-bans" element={<ItemBansView />} />
              <Route path="/spawn-limits" element={<SpawnLimitsView />} />
              <Route path="/player-stream-log/:clusterId" element={<PlayerStreamLogView />} />
              <Route path="/player-stream-log" element={<PlayerStreamLogView />} />
              <Route path="/notes-warnings" element={<NotesWarningsView />} />
              <Route path="/bans" element={<BansView />} />
              <Route path="/evidence" element={<EvidenceView />} />
              {canViewAppeals && (
                <>
                  <Route path="/appeals" element={<AppealsView />} />
                  <Route path="/appeals/:id" element={<AppealDetailView />} />
                </>
              )}
              {canViewIncidents && (
                <>
                  <Route path="/incidents" element={<IncidentsListView />} />
                  <Route path="/incidents/:id" element={<IncidentDetailView />} />
                </>
              )}
              {canViewCalendar && (
                <Route path="/calendar" element={<CalendarListView />} />
              )}
              {canViewAtlas && (
                <Route path="/atlas" element={<AtlasView />} />
              )}
              {canViewStaff && (
                <>
                  <Route path="/staff" element={<StaffManagerView />} />
                  <Route path="/staff/teams" element={<StaffTeamsView />} />
                  <Route path="/staff/tickets" element={<StaffTicketsView />} />
                  <Route path="/staff/notes" element={<StaffNotesView />} />
                  <Route path="/staff/activity" element={<StaffActivityView />} />
                  <Route path="/staff/migrations" element={<StaffMigrationsView />} />
                  <Route path="/staff/provisioning" element={<ModpackProvisioningView approvals />} />
                  <Route path="/staff/:discordId" element={<StaffProfileView />} />
                </>
              )}
              {canUseDataController && (
                <Route path="/staff/data-controller" element={<DataControllerView />} />
              )}
              {canViewChunkThrottle && (
                <>
                  <Route path="/chunk-throttle" element={<ChunkThrottleView />} />
                  <Route path="/chunk-throttle/:fqdn" element={<ChunkThrottleServerView />} />
                </>
              )}
              <Route path="/votes" element={<VotesView />} />
              <Route path="/update-advisor" element={<UpdateAdvisorView />} />
              {canViewModpackNotes && (
                <Route path="/modpack-notes" element={<ModpackNotesView />} />
              )}
              {canViewModpackConfigsHere && (
                <>
                  <Route path="/modpack-configs" element={<ModpackConfigsView />} />
                  <Route path="/modpack-configs/provisioning" element={<ModpackProvisioningView />} />
                  <Route path="/modpack-configs/:address" element={<ModpackConfigDetailView />} />
                </>
              )}
              {canViewScheduledCommandsHere && (
                <>
                  <Route path="/scheduled-commands" element={<ScheduledCommandsView />} />
                  <Route path="/scheduled-commands/library/:entryId" element={<TellrawLibraryEntryView />} />
                  <Route path="/scheduled-commands/:scheduleId" element={<ScheduledCommandEditorView />} />
                </>
              )}
              {canViewPartnershipsHere && (
                <>
                  <Route path="/partnerships" element={<PartnershipsView />} />
                  <Route path="/partnerships/:id" element={<PartnershipDetailView />} />
                  <Route path="/partnerships/partners/:creatorId" element={<PartnerView />} />
                </>
              )}
              <Route path="/modpacks" element={<ModpackBrowserView />} />
              <Route path="/modpacks/id/:apogeeId" element={<ModpackDetailView />} />
              <Route path="/modpacks/:mpiId" element={<ModpackDetailView />} />
              {canViewBuildTeam && (
                <>
                  <Route path="/build-team" element={<BuildTeamView />} />
                  <Route path="/build-team/projects/:id" element={<BuildProjectDetailView />} />
                  <Route path="/build-team/builds/:id" element={<BuildItemDetailView />} />
                </>
              )}
              {canViewEventTeam && (
                <>
                  <Route path="/event-team" element={<EventTeamView />} />
                  <Route path="/event-team/events/:id" element={<EventItemDetailView />} />
                  <Route path="/event-team/projects/:id" element={<EventProjectDetailView />} />
                  <Route path="/event-team/members/:id" element={<EventMemberDetailView />} />
                  <Route path="/event-team/checks/:id" element={<EventCheckDetailView />} />
                </>
              )}
              {isDev && <Route path="/dev/quest-viewer" element={<QuestViewerDevView />} />}
              {canViewWorkspaces && (
                <>
                  <Route path="/workspaces" element={<WorkspacesView />} />
                  <Route path="/workspaces/:boardId" element={<WorkspaceBoardView />} />
                </>
              )}
            </Routes>
            </div>
            </LimitedAccessBoundary>
          </main>
          
          {!isMobile && !isTeamScopedStaff && (
            <button
              onClick={() => setIsSearchModalOpen(true)}
              className="fixed bottom-6 right-6 z-30 btn-primary !rounded-full !w-12 !h-12 !p-0 shadow-popover"
              title="Open Player Search (⌘K)"
              aria-label="Open Player Search"
            >
              <Search className="w-5 h-5" />
            </button>
          )}

          {isMobile && (
            <MobileBottomNav
              favoriteItems={favoriteItems.slice(0, 5)}
              limitedNavItems={
                isTeamScopedStaff
                  ? visibleNavigationItems.slice(0, 5)
                  : null
              }
            />
          )}

          {isSearchModalOpen && 
            <SearchModal 
              isOpen={isSearchModalOpen}
              onClose={() => setIsSearchModalOpen(false)} 
              initialQuery={searchQuery} 
              onResultClick={handleSearchResultClick}
            />
          }
        </div>
      </div>
    </div>
  );
}

const AppWrapper = () => (
  <ThemeProvider>
    <ChatTabsProvider>
      <CommandLogTabsProvider>
        <AuthProvider>
          <UserPreferencesProvider>
            <App />
          </UserPreferencesProvider>
        </AuthProvider>
      </CommandLogTabsProvider>
    </ChatTabsProvider>
  </ThemeProvider>
);

export default AppWrapper;
